import { Paper, Stack, Text } from '@mantine/core';
import { MoneyText } from './MoneyText';

/** The chart-row fields the tooltip reads, in dollars (what the chart plots). */
export interface RevenueSplitChartRow {
  /** The plotted series: the revenue total, in dollars. */
  readonly Revenue?: number;
  readonly Sales: number;
  readonly 'Traded away': number;
}

/** Type guard: does this recharts payload row carry the split fields? */
function isSplitRow(value: unknown): value is RevenueSplitChartRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.Sales === 'number' && typeof v['Traded away'] === 'number';
}

/** Props recharts hands a custom `Tooltip` `content` renderer (the subset used). */
export interface RevenueSplitTooltipProps {
  readonly active?: boolean;
  readonly label?: string | number;
  readonly payload?: readonly { readonly payload?: unknown }[];
}

/**
 * RevenueSplitTooltip - the hover card for a revenue chart that plots ONE
 * series (the total) but names its two parts on hover (vendor#1453).
 *
 * Shell: any staff shell. Never (kiosk).
 *
 * Contract: the chart's data rows carry `Sales` and `Traded away` in dollars
 * beside the plotted series; those are not added as series, so the chart keeps
 * its single bar/area and the total. The hover card leads with the Revenue total, then the parts. Renders nothing
 * when the row lacks the parts.
 * Both parts are money in (`--money-in`).
 */
export function RevenueSplitTooltip({ active, label, payload }: RevenueSplitTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !isSplitRow(row)) return null;
  const saleCents = Math.round(row.Sales * 100);
  const tradeCents = Math.round(row['Traded away'] * 100);
  const revenue = typeof row.Revenue === 'number' ? row.Revenue : null;
  return (
    <Paper withBorder shadow="sm" radius="md" px="sm" py={6} data-testid="revenue-split-tooltip">
      <Stack gap={2}>
        {label != null && (
          <Text fz={11} c="dimmed">
            {label}
          </Text>
        )}
        {revenue != null && (
          <Text fz={12}>
            Revenue <MoneyText cents={Math.round(revenue * 100)} size={12} />
          </Text>
        )}
        <Text fz={12} c="dimmed">
          Sales <MoneyText cents={saleCents} role={saleCents === 0 ? 'flat' : 'in'} size={12} />
        </Text>
        <Text fz={12} c="dimmed">
          Traded away{' '}
          <MoneyText cents={tradeCents} role={tradeCents === 0 ? 'flat' : 'in'} size={12} />
        </Text>
      </Stack>
    </Paper>
  );
}
